/**
 * R-131-SUP-A — ImpersonationBanner with handleEnd ordered before effects.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, PenLine, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { auth } from '../../firebase';
import {
  clearImpersonationStash,
  refreshImpersonationFromUser,
  signInWithImpersonationToken,
  type ImpersonationState,
} from '../../lib/impersonation';
import { callAdminFunction } from '../admin/adminApi';
import { Button } from '../ui/button';

const MIN_WRITE_REASON_LEN = 20;

interface EndSessionResult {
  token: string;
  actorEmail: string;
}

interface EnableWriteResult {
  token: string;
  readOnly: boolean;
  expiresAt: number;
}

export function ImpersonationBanner() {
  const [state, setState] = useState<ImpersonationState | null>(null);
  const [ending, setEnding] = useState(false);
  const [writeOpen, setWriteOpen] = useState(false);
  const [writeReason, setWriteReason] = useState('');
  const [enablingWrite, setEnablingWrite] = useState(false);

  const sync = useCallback(async (forceRefresh = false) => {
    const user = auth.currentUser;
    const next = await refreshImpersonationFromUser(user, forceRefresh);
    setState(next);
    return next;
  }, []);

  const handleEnd = useCallback(async (reason = 'user_ended') => {
    if (ending) return;
    setEnding(true);
    try {
      const result = await callAdminFunction<EndSessionResult>('endImpersonationSession', { reason });
      clearImpersonationStash();
      await signInWithImpersonationToken(result.token);
      toast.message('Impersonation ended — restored admin session');
      setWriteOpen(false);
      await sync(true);
    } catch (e) {
      toast.error(`Failed to end session: ${(e as Error).message}`);
    } finally {
      setEnding(false);
    }
  }, [ending, sync]);

  useEffect(() => {
    void sync();
    const unsub = auth.onAuthStateChanged(() => { void sync(true); });
    const tick = window.setInterval(() => { void sync(); }, 30_000);
    return () => {
      unsub();
      window.clearInterval(tick);
    };
  }, [sync]);

  useEffect(() => {
    if (!state?.active) return undefined;
    const msLeft = state.expiresAt - Date.now();
    if (msLeft <= 0) {
      void handleEnd('timer_expired');
      return undefined;
    }
    const timer = window.setTimeout(() => { void handleEnd('timer_expired'); }, msLeft);
    return () => window.clearTimeout(timer);
  }, [state?.expiresAt, state?.active, handleEnd]);

  const handleEnableWrite = async () => {
    if (writeReason.trim().length < MIN_WRITE_REASON_LEN) {
      toast.error(`Write reason must be at least ${MIN_WRITE_REASON_LEN} characters`);
      return;
    }
    setEnablingWrite(true);
    try {
      const result = await callAdminFunction<EnableWriteResult>('enableImpersonationWrite', {
        reason: writeReason.trim(),
      });
      await signInWithImpersonationToken(result.token);
      toast.success('Write mode enabled — mutations will be audit-logged');
      setWriteOpen(false);
      setWriteReason('');
      await sync(true);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setEnablingWrite(false);
    }
  };

  if (!state?.active) return null;

  return (
    <div
      className="sticky top-0 z-[60] flex flex-col gap-2 px-3 py-2 text-xs bg-danger/20 text-danger border-b border-danger/40 shrink-0"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-mono uppercase tracking-wide">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            VIEWING AS {state.targetEmail ?? state.targetUid}
            {' — '}actor: {state.actorEmail}
            {' — '}ends in {state.minutesRemaining} min
            {state.readOnly ? ' — READ-ONLY' : ' — READ-WRITE'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {state.readOnly && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-[10px] uppercase"
              onClick={() => setWriteOpen(v => !v)}
              data-testid="impersonation-enable-write"
            >
              <PenLine className="w-3 h-3 mr-1" />
              Read-Write
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-[10px] uppercase"
            disabled={ending}
            onClick={() => void handleEnd('user_clicked_end')}
            data-testid="impersonation-end-session"
          >
            {ending ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
            End session
          </Button>
        </div>
      </div>

      {writeOpen && state.readOnly && (
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end max-w-3xl">
          <div className="flex-1 w-full">
            <label htmlFor="impersonation-write-reason" className="sr-only">Write mode reason</label>
            <textarea
              id="impersonation-write-reason"
              className="w-full min-h-[56px] px-2 py-1 text-xs border border-danger/40 bg-background font-mono"
              placeholder={`Why write access is needed (min ${MIN_WRITE_REASON_LEN} chars)`}
              value={writeReason}
              onChange={e => setWriteReason(e.target.value)}
              data-testid="impersonation-write-reason"
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="h-8 text-[10px] uppercase shrink-0"
            disabled={enablingWrite || writeReason.trim().length < MIN_WRITE_REASON_LEN}
            onClick={() => void handleEnableWrite()}
          >
            {enablingWrite ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Enable write'}
          </Button>
        </div>
      )}
    </div>
  );
}
